/**
 * SIL-1346 - the Active Verification explainer modal (spec §4.4, handoff §4).
 * One "Done" button, no Cancel. Every sentence comes from
 * `copy/activeVerification.ts`; this file contains layout and no copy, which
 * is the acceptance criterion for the issue (a grep for an explainer sentence
 * must find only the copy module).
 *
 * `ActiveVerificationExplainerTrigger` is the one-line drop-in for the listing
 * detail's "What is tested" link and the node drawer's Active Verification
 * section - same shape as `asset-v2/FmvBasisDrawer.tsx`'s `FmvBasisTrigger`,
 * which owns its own open state so a caller needs no `useState`.
 */
import { useEffect, useId, useState } from 'react';
import { Card } from '../Card.js';
import { Button } from '../Button.js';
import {
  ACTIVE_VERIFICATION_CHECKS,
  ACTIVE_VERIFICATION_CHECKS_HEADING,
  ACTIVE_VERIFICATION_CHECK_COLUMNS,
  ACTIVE_VERIFICATION_DONE_LABEL,
  ACTIVE_VERIFICATION_INTRO,
  ACTIVE_VERIFICATION_NOT_COVERED,
  ACTIVE_VERIFICATION_NOT_COVERED_HEADING,
  ACTIVE_VERIFICATION_TIERS,
  ACTIVE_VERIFICATION_TIERS_HEADING,
  ACTIVE_VERIFICATION_TITLE,
  ACTIVE_VERIFICATION_TRIGGER_LABEL,
  ACTIVE_VERIFICATION_VERDICTS,
  ACTIVE_VERIFICATION_VERDICTS_HEADING,
  type ActiveVerificationVerdictCopy,
} from './copy/activeVerification';

const VERDICT_DOT: Record<ActiveVerificationVerdictCopy['tone'], string> = {
  success: 'bg-emerald-500',
  warn: 'bg-amber-500',
  error: 'bg-red-500',
};

function SectionHeading({ children }: { children: string }) {
  return (
    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{children}</h3>
  );
}

export function ActiveVerificationExplainerModal({ onClose }: { onClose: () => void }) {
  const titleId = useId();

  // Escape closes - the modal has no Cancel button, so the keyboard exit is
  // the only non-pointer way out.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4"
      onClick={onClose}
    >
      <Card
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="max-h-full w-full max-w-2xl overflow-y-auto shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} className="text-base font-semibold text-foreground">
          {ACTIVE_VERIFICATION_TITLE}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">{ACTIVE_VERIFICATION_INTRO}</p>

        <section className="mt-5">
          <SectionHeading>{ACTIVE_VERIFICATION_CHECKS_HEADING}</SectionHeading>
          <table className="mt-2 w-full border-collapse text-left text-xs">
            <thead>
              <tr className="border-b border-border">
                {ACTIVE_VERIFICATION_CHECK_COLUMNS.map((column) => (
                  <th key={column} className="py-1.5 pr-3 font-medium text-muted-foreground">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ACTIVE_VERIFICATION_CHECKS.map((check) => (
                <tr key={check.id} className="border-b border-border/60 last:border-0">
                  <td className="py-1.5 pr-3 font-medium text-foreground">{check.name}</td>
                  <td className="py-1.5 pr-3 text-muted-foreground">{check.measures}</td>
                  <td className="py-1.5 text-muted-foreground">{check.passCondition}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="mt-5">
          <SectionHeading>{ACTIVE_VERIFICATION_VERDICTS_HEADING}</SectionHeading>
          <ul className="mt-2 space-y-1.5">
            {ACTIVE_VERIFICATION_VERDICTS.map((verdict) => (
              <li key={verdict.id} className="flex gap-2 text-xs">
                <span className={`mt-1.5 size-1.5 shrink-0 rounded-full ${VERDICT_DOT[verdict.tone]}`} aria-hidden />
                <span>
                  <span className="font-medium text-foreground">{verdict.label}</span>
                  {verdict.detail ? <span className="text-muted-foreground"> - {verdict.detail}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-5">
          <SectionHeading>{ACTIVE_VERIFICATION_TIERS_HEADING}</SectionHeading>
          <ul className="mt-2 space-y-1.5">
            {ACTIVE_VERIFICATION_TIERS.map((tier) => (
              <li key={tier.tier} className="text-xs">
                <span className="font-medium text-foreground">Tier {tier.tier}</span>{' '}
                <span className="text-muted-foreground">{tier.label}</span>
                {tier.note ? <span className="text-muted-foreground"> - {tier.note}</span> : null}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-5">
          <SectionHeading>{ACTIVE_VERIFICATION_NOT_COVERED_HEADING}</SectionHeading>
          <p className="mt-2 text-xs text-muted-foreground">{ACTIVE_VERIFICATION_NOT_COVERED}</p>
        </section>

        <div className="mt-6 flex justify-end">
          <Button onClick={onClose}>{ACTIVE_VERIFICATION_DONE_LABEL}</Button>
        </div>
      </Card>
    </div>
  );
}

/**
 * Text-link trigger that owns its own open state. `label` defaults to the
 * handoff's "What is tested"; My Assets passes
 * `ACTIVE_VERIFICATION_HOW_IT_WORKS_LABEL` instead.
 */
export function ActiveVerificationExplainerTrigger({
  label = ACTIVE_VERIFICATION_TRIGGER_LABEL,
  className,
}: {
  label?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={className ?? 'text-xs font-medium text-brand-600 hover:underline'}
      >
        {label}
      </button>
      {open && <ActiveVerificationExplainerModal onClose={() => setOpen(false)} />}
    </>
  );
}
